import hash from 'object-hash';
import {
  LabelOrigin,
  LabelType,
  LabelValueDetails,
  LabelValueSource,
  TimeSignal
} from 'graphql/types';
import {
  NotesGroupByStrategy,
  UNCATEGORIZED_GROUP_KEY
} from './NotesGroupBySorter';

export class LabelGroupByStrategy implements NotesGroupByStrategy {
  private readonly date: string;

  constructor(date: string) {
    this.date = date;
  }

  generateGroups(notes: TimeSignal[]): Record<string, TimeSignal[]> {
    return this.groupNotes(notes, this.date);
  }

  private groupNotes(
    notes: TimeSignal[],
    date: string
  ): Record<string, TimeSignal[]> {
    const timeSignalGroups = new Map<string, TimeSignal[]>();

    this.handleUncategorizedNotes(notes, timeSignalGroups, date);
    this.handleCategorizedNotes(notes, timeSignalGroups, date);

    return Object.fromEntries(timeSignalGroups);
  }

  private handleUncategorizedNotes(
    notes: TimeSignal[],
    timeSignalGroups: Map<string, TimeSignal[]>,
    date: string
  ) {
    const uncategorizedNotes = notes.filter(
      this.isUncategorizedNote.bind(this)
    );
    if (uncategorizedNotes.length > 0) {
      timeSignalGroups.set(
        `${UNCATEGORIZED_GROUP_KEY}-${date}`,
        uncategorizedNotes
      );
    }
  }

  private handleCategorizedNotes(
    notes: TimeSignal[],
    timeSignalGroups: Map<string, TimeSignal[]>,
    date: string
  ) {
    const categorizedNotes = notes.filter(
      (note: TimeSignal) => !this.isUncategorizedNote(note)
    );
    if (categorizedNotes.length === 0) return;

    for (let i = 0; i < categorizedNotes.length; i++) {
      const currentNote = categorizedNotes[i];
      const labels = this.getLabelAndLabelValueArray(currentNote);
      const labelHash = hash(labels, { algorithm: 'sha1' });
      this.appendNoteToGroup(
        timeSignalGroups,
        `labelGroup-${labelHash}-${date}`,
        currentNote
      );
    }
  }

  private isUncategorizedNote(note: TimeSignal) {
    const groupingLabels = this.getGroupingLabels(note);
    return groupingLabels.some((labelValue: LabelValueDetails) => {
      return (
        labelValue.valueSource === LabelValueSource.Suggestion ||
        (!labelValue?.value?.idValue &&
          !labelValue?.value?.numericValue &&
          !labelValue?.value?.stringValue)
      );
    });
  }

  private appendNoteToGroup(
    map: Map<string, TimeSignal[]>,
    key: string,
    note: TimeSignal
  ): void {
    const group = map.get(key);
    if (!group) {
      map.set(key, [note]);
    } else {
      group.push(note);
    }
  }

  private getGroupingLabels(note: TimeSignal): LabelValueDetails[] {
    const groupingLabels = note.labelValues.filter(
      (labelValue: LabelValueDetails) => {
        return (
          labelValue.label?.type !== LabelType.Comment &&
          labelValue.label?.origin === LabelOrigin.Timesheet
        );
      }
    );

    return groupingLabels;
  }

  private getLabelAndLabelValueArray(note: TimeSignal) {
    const groupingLabels = this.getGroupingLabels(note);

    return groupingLabels.map((labelValue: LabelValueDetails) => ({
      label: {
        ...labelValue.label
      },
      value: {
        ...labelValue.value
      }
    }));
  }
}
